import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Settings, Database, Key, Cpu, XCircle, Loader2 } from 'lucide-react';
import ToolCard from '../shared/ToolCard';
import api from '../../../services/api';
import toast from 'react-hot-toast';

const InfrastructureTools = () => {
  const [selectedTool, setSelectedTool] = useState(null);
  const [isLoading, setIsLoading] = useState({});
  const [serverStats, setServerStats] = useState(null);
  const [maintenanceStatus, setMaintenanceStatus] = useState({
    isLoading: false,
    isEnabled: false,
    message: ''
  });

  const infrastructureTools = [
    {
      id: 'serverTools',
      icon: Settings,
      label: 'Server Management',
      description: 'Server statistics and maintenance mode controls',
      action: 'Manage Server'
    },
    {
      id: 'cacheControl',
      icon: Database,
      label: 'Cache Management', 
      description: 'Clear and manage application caches',
      action: 'Manage Cache'
    },
    {
      id: 'apiKeys',
      icon: Key,
      label: 'API Key Management',
      description: 'Create and manage API keys for system access',
      action: 'Manage Keys'
    }
  ];

  const handleToolClick = (tool) => {
    setSelectedTool(selectedTool === tool.id ? null : tool.id);
  };

  const getServerStats = async () => {
    try {
      setIsLoading({ ...isLoading, stats: true });
      const response = await api.get('/api/dev/server-stats/');
      setServerStats(response.data);
      toast.success('Server statistics loaded');
    } catch (error) {
      console.error('Error loading server stats:', error);
      toast.error('Failed to load server statistics');
    } finally {
      setIsLoading({ ...isLoading, stats: false });
    }
  };

  const toggleMaintenanceMode = async () => {
    try {
      setMaintenanceStatus({ ...maintenanceStatus, isLoading: true });
      const response = await api.post('/api/dev/maintenance-mode/');
      
      setMaintenanceStatus({
        isLoading: false,
        isEnabled: response.data.enabled,
        message: `Maintenance mode ${response.data.enabled ? 'enabled' : 'disabled'}`
      });
      
      toast.success(
        <div>
          <p className="font-semibold">Maintenance mode {response.data.enabled ? 'enabled' : 'disabled'}</p>
          <p className="text-sm mt-1">{response.data.message || ''}</p>
        </div>
      );
    } catch (error) {
      console.error('Error toggling maintenance mode:', error);
      setMaintenanceStatus({
        isLoading: false,
        isEnabled: maintenanceStatus.isEnabled,
        message: 'Error toggling maintenance mode'
      });
      toast.error('Failed to toggle maintenance mode');
    }
  };

  const clearCache = async (cacheType) => {
    try {
      setIsLoading({ ...isLoading, [cacheType]: true });
      await api.post('/api/dev/clear-cache/', { cache_type: cacheType });
      toast.success(`${cacheType} cache cleared successfully`);
    } catch (error) {
      console.error(`Error clearing ${cacheType} cache:`, error);
      toast.error(`Failed to clear ${cacheType} cache`);
    } finally {
      setIsLoading({ ...isLoading, [cacheType]: false });
    }
  };

  const renderToolContent = (toolId) => {
    switch (toolId) {
      case 'serverTools':
        return (
          <div className="card mt-6 space-y-6 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Server Management</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={getServerStats}
                disabled={isLoading.stats}
                className="btn btn-primary btn-lg w-full"
              >
                {isLoading.stats ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Cpu className="h-4 w-4" />
                )}
                <span>Load Server Stats</span>
              </button>

              <button
                onClick={toggleMaintenanceMode}
                disabled={maintenanceStatus.isLoading}
                className="btn btn-secondary btn-lg w-full"
              >
                {maintenanceStatus.isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Settings className="h-4 w-4 text-warning-600 dark:text-warning-400" />
                )}
                <span>Toggle Maintenance</span>
              </button>
            </div>

            {serverStats && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                <div className="mb-4 flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Server Statistics</h4>
                  <button
                    onClick={() => setServerStats(null)}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/[0.06] dark:hover:text-gray-200"
                  >
                    <XCircle className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(serverStats).map(([key, value]) => (
                    <div key={key} className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                      <h5 className="text-sm font-medium capitalize text-gray-700 dark:text-gray-200">{key.replace('_', ' ')}</h5>
                      <p className="mt-1 break-words text-sm text-gray-600 dark:text-gray-400">
                        {typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'cacheControl':
        return (
          <div className="card mt-6 p-6">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Cache Management</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['template', 'view', 'session', 'permissions', 'all'].map(cacheType => (
                <button
                  key={cacheType}
                  onClick={() => clearCache(cacheType)}
                  disabled={isLoading[cacheType]}
                  className={`btn btn-lg w-full ${cacheType === 'all' ? 'btn-danger' : 'btn-danger-soft border border-danger-500/25'}`}
                >
                  {isLoading[cacheType] ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Database className="h-4 w-4" />
                  )}
                  <span>Clear {cacheType.charAt(0).toUpperCase() + cacheType.slice(1)}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 'apiKeys':
        return (
          <div className="card mt-6 p-6">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">API Key Management</h3>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              API key management interface would go here. This would typically include:
            </p>
            <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <li>• Create new API keys</li>
              <li>• View existing keys</li>
              <li>• Revoke/disable keys</li>
              <li>• Set key permissions</li>
            </ul>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {infrastructureTools.map((tool) => (
          <ToolCard
            key={tool.id}
            tool={tool}
            onClick={handleToolClick}
            isLoading={isLoading[tool.id]}
            className={selectedTool === tool.id ? 'ring-2 ring-primary-500' : ''}
          />
        ))}
      </div>

      {/* Render selected tool content */}
      {selectedTool && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          {renderToolContent(selectedTool)}
        </motion.div>
      )}
    </div>
  );
};

export default InfrastructureTools;
